import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import { XMarkIcon, VideoCameraIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  usagePeriodKey,
  summarizeVideoUsage,
  parseAllowanceForm,
  VIDEO_ALLOWANCE_DEFAULTS,
  type VideoSessionsSettings,
} from '../../utils/videoAllowance';

/**
 * Per-organisation video-session allowance.
 *
 * Video avatars are a paid add-on. Before this dialog the row button could
 * only switch the add-on on or off; the monthly allowance it seeded could not
 * be changed anywhere in the product, and this month's usage was invisible —
 * so the first anyone knew of an exhausted allowance was a learner's video
 * scenario silently opening as a voice call. This is where the platform sets
 * the numbers the student API enforces, sees what has been used, and can
 * reset a month (test traffic, a goodwill top-up) without touching Firestore.
 */

interface VideoAllowanceModalProps {
  org: {
    id: string;
    name: string;
    videoSessions?: Partial<VideoSessionsSettings> | null;
  };
  onClose: () => void;
  /** Called with the saved settings so the list can update without a refetch. */
  onSaved: (settings: VideoSessionsSettings) => void;
}

const inputClass =
  'w-full px-4 py-2.5 border rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

const monthLabel = (period: string) => {
  const [y, m] = period.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const VideoAllowanceModal: React.FC<VideoAllowanceModalProps> = ({ org, onClose, onSaved }) => {
  const { showToast } = useToast();
  const stored = org.videoSessions || {};

  const [enabled, setEnabled] = useState<boolean>(!!stored.enabled);
  const [monthlyMinutes, setMonthlyMinutes] = useState<string>(
    stored.monthlyMinutes != null ? String(stored.monthlyMinutes) : '',
  );
  const [maxSessionMinutes, setMaxSessionMinutes] = useState<string>(
    stored.maxSessionMinutes != null ? String(stored.maxSessionMinutes) : '',
  );
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const period = usagePeriodKey();
  const [usedMinutes, setUsedMinutes] = useState<number | null>(null); // null = loading
  const [usageError, setUsageError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  // This month's metered minutes. Read once on open; a reset updates it locally.
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'organizations', org.id, 'usage', period))
      .then(snap => {
        if (cancelled) return;
        setUsedMinutes(snap.exists() ? Number(snap.data()?.videoMinutes) || 0 : 0);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Could not read video usage:', err);
        setUsageError('Could not load this month\'s usage.');
        setUsedMinutes(0);
      });
    return () => { cancelled = true; };
  }, [org.id, period]);

  // Preview against the numbers currently typed, so raising the allowance
  // shows the effect before saving.
  const parsed = parseAllowanceForm({ enabled, monthlyMinutes, maxSessionMinutes });
  const previewAllowance = parsed.ok ? parsed.value.monthlyMinutes : Number(stored.monthlyMinutes) || 0;
  const usage = summarizeVideoUsage({ monthlyMinutes: previewAllowance, usedMinutes: usedMinutes ?? 0 });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = parseAllowanceForm({ enabled, monthlyMinutes, maxSessionMinutes });
    if (!result.ok) {
      setFieldError({ field: result.field, message: result.message });
      return;
    }
    setFieldError(null);
    setSaving(true);
    try {
      await updateDoc(doc(db, 'organizations', org.id), {
        videoSessions: result.value,
        updatedAt: serverTimestamp(),
      });
      showToast(
        result.value.enabled
          ? `Video sessions on for ${org.name} — ${result.value.monthlyMinutes} min/month, ${result.value.maxSessionMinutes} min per session`
          : `Video sessions off for ${org.name}`,
        'success',
      );
      onSaved(result.value);
      onClose();
    } catch (err: any) {
      console.error('Error saving video allowance:', err);
      showToast(`Failed to save: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(
      `Reset ${org.name}'s video usage for ${monthLabel(period)} to 0 minutes?\n\nThe ${usage.used} minutes recorded so far will no longer count against the allowance. This cannot be undone.`,
    )) return;
    setResetting(true);
    try {
      await setDoc(
        doc(db, 'organizations', org.id, 'usage', period),
        { videoMinutes: 0, resetAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true },
      );
      setUsedMinutes(0);
      showToast(`Video usage for ${monthLabel(period)} reset to 0`, 'success');
    } catch (err: any) {
      console.error('Error resetting video usage:', err);
      showToast(`Failed to reset usage: ${err.message}`, 'error');
    } finally {
      setResetting(false);
    }
  };

  const barColor = usage.exhausted
    ? 'bg-red-500'
    : usage.percentUsed >= 80
      ? 'bg-amber-500'
      : 'bg-indigo-500';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="video-allowance-title"
        className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-transparent p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
              <VideoCameraIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 id="video-allowance-title" className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                Video sessions
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{org.name} · paid add-on</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSave} className="space-y-5">
          {/* On / off */}
          <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">Enable video sessions</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Managers can mark scenarios as video; learners meet a video avatar.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label="Enable video sessions"
              onClick={() => {
                const next = !enabled;
                setEnabled(next);
                // First switch-on seeds a starter allowance, as the old toggle did.
                if (next && monthlyMinutes.trim() === '') {
                  setMonthlyMinutes(String(VIDEO_ALLOWANCE_DEFAULTS.monthlyMinutes));
                }
              }}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Limits */}
          <div className={`grid grid-cols-2 gap-4 ${enabled ? '' : 'opacity-60'}`}>
            <div>
              <label htmlFor="video-monthly-minutes" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Monthly allowance
              </label>
              <div className="relative">
                <input
                  id="video-monthly-minutes"
                  type="text"
                  inputMode="numeric"
                  value={monthlyMinutes}
                  onChange={(e) => { setMonthlyMinutes(e.target.value); setFieldError(null); }}
                  disabled={!enabled}
                  aria-invalid={fieldError?.field === 'monthlyMinutes'}
                  className={`${inputClass} pr-12 ${fieldError?.field === 'monthlyMinutes' ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  placeholder="300"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">min</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Resets on the 1st of each month.</p>
            </div>
            <div>
              <label htmlFor="video-session-cap" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Per-session cap
              </label>
              <div className="relative">
                <input
                  id="video-session-cap"
                  type="text"
                  inputMode="numeric"
                  value={maxSessionMinutes}
                  onChange={(e) => { setMaxSessionMinutes(e.target.value); setFieldError(null); }}
                  disabled={!enabled}
                  aria-invalid={fieldError?.field === 'maxSessionMinutes'}
                  className={`${inputClass} pr-12 ${fieldError?.field === 'maxSessionMinutes' ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  placeholder={String(VIDEO_ALLOWANCE_DEFAULTS.maxSessionMinutes)}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">min</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Longest a single call can run.</p>
            </div>
          </div>

          {fieldError && (
            <p role="alert" className="text-xs text-red-600 dark:text-red-400 -mt-2">{fieldError.message}</p>
          )}
          {!fieldError && parsed.ok && parsed.warning && (
            <p role="status" className="text-xs text-amber-600 dark:text-amber-400 -mt-2">{parsed.warning}</p>
          )}

          {/* This month */}
          <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                Usage · {monthLabel(period)}
              </div>
              {usedMinutes === null ? (
                <span className="text-xs text-gray-400">Loading…</span>
              ) : (
                <span className={`text-xs font-medium ${usage.exhausted && enabled ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>
                  {usage.used} of {usage.allowance} min used
                </span>
              )}
            </div>
            <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={usage.percentUsed} aria-label="Share of monthly allowance used">
              <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${usedMinutes === null ? 0 : usage.percentUsed}%` }} />
            </div>
            <div className="flex items-center justify-between mt-2 gap-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {usageError
                  ? usageError
                  : usedMinutes === null
                    ? ' '
                    : usage.exhausted && enabled
                      ? 'Allowance used up — new video sessions run as voice until raised or reset.'
                      : `${usage.remaining} min remaining this month.`}
              </p>
              <button
                type="button"
                onClick={handleReset}
                disabled={resetting || usedMinutes === null || (usedMinutes ?? 0) <= 0}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                title="Set this month's metered minutes back to 0"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 ${resetting ? 'animate-spin' : ''}`} />
                {resetting ? 'Resetting…' : 'Reset month'}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-2xl font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-semibold text-sm transition-colors disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VideoAllowanceModal;
